<template>
    <head>
        <title>{{ $t('campaign_version_two.meta_title') }}</title>
    </head>

    <CampaignLayoutV2>
        <div class="w-full min-h-[50px] relative">
            <div class="grid grid-cols-2 absolute top-0 left-0 right-0 bottom-0">
                <div class="bg-[#ECF2FC]" />
                <div class="bg-[#ECF2FC] lg:bg-[#F7F9FC]" />
            </div>

            <!-- HERO SECTION -->
            <div class="relative mx-auto w-[95%] sm:w-[70%] md:w-[50%] lg:w-[90%] min-w-[290px] max-w-[1170px] min-h-[50px]">
                <img
                    src="/images/campaigns/version-two/linear-frame.svg"
                    class="hidden lg:block absolute top-0 -right-24 max-w-[700px]"
                    alt="Linear frame"
                />

                <!-- HEADER -->
                <header class="grid grid-cols-2 lg:grid-cols-4" style="height: 100px">
                    <div class="lg:col-span-3 flex items-center bg-[#ECF2FC]">
                        <img
                            src="/images/campaigns/logo.svg"
                            class="w-[110px] lg:w-[132px]"
                            alt="Nova logo"
                        />
                    </div>

                    <div class="relative">
                        <div class="absolute right-0 lg:right-auto h-full flex items-center">
                            <a href="tel:052519746" class="flex ml-8">
                                <div class="flex items-center">
                                    <img
                                        src="/images/campaigns/version-two/phone-icon.svg"
                                        class="w-[16px] lg:w-[18px] mr-1"
                                        alt="Phone icon"
                                    />

                                    <span class="text-[16px] lg:text-[20px] font-bold text-[#108FE5]">
                                        {{ $t('campaign_version_two.phone_number') }}
                                    </span>
                                </div>
                            </a>
                        </div>
                    </div>
                </header>

                <div class="grid lg:grid-cols-4">
                    <div class="lg:col-span-3 bg-[#ECF2FC]" style="padding-bottom: 40px">
                        <div class="flex flex-col lg:max-w-[73%] mt-2 lg:mt-10">
                            <h1 class="headline text-[26px] lg:text-[42px] font-bold leading-10 text-[#2C2C4F]">
                                {{ $t('campaign_version_two.Get an') }} <span class="underline underline-offset-4 text-[#F6C407]"><span class="text-[#2C2C4F]">{{ $t('campaign_version_two.instant') }}</span></span> {{ $t('campaign_version_two.solar quote') }}
                            </h1>

                            <p
                                class="leading-normal text-[14px] lg:text-[16px] text-[#2C2C4F]"
                                v-html="$t('campaign_version_two.subline')"
                            />

                            <div class="mt-2 lg:mt-10">
                                <SliderCheckListItem
                                    v-for="(item, index) in heroChecklist"
                                    :key="`hero-check-list-item-${index}`"
                                >
                                    <p v-html="item"></p>
                                </SliderCheckListItem>
                            </div>

                            <div class="mt-6 lg:mt-11">
                                <div class="flex lg:inline-flex flex-col">
                                    <button
                                        @click="scrollToElement('quotesContainer')"
                                        class="bg-[#1EB26D] text-[15px] lg:text-[18px] font-semibold text-white hover:bg-opacity-90 rounded-full px-3 sm:px-16 py-3 leading-snug">
                                        {{ $t('campaign_version_two.Start your personalized quote') }}
                                    </button>

                                    <div class="flex justify-between items-center lg:mt-5">
                                        <div class="flex items-center scale-[0.85] sm:scale-[1.0]">
                                            <img
                                                src="/images/campaigns/google-logo-icon.svg"
                                                class="w-[24px] mr-2"
                                                alt="Google logo icon"
                                            />

                                            <img
                                                src="/images/campaigns/rating-stars.svg"
                                                alt="Google's rating stars"
                                            />

                                            <p class="text-[#1B2F51] font-bold leading-snug text-[14px] ml-2">
                                                4,7/5
                                            </p>
                                        </div>

                                        <div>
                                            <img
                                                src="/images/campaigns/version-two/hero-premium-partner.svg"
                                                class="max-w-[140px] sm:max-w-[181px]"
                                                alt="Premium partner logo"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="lg:relative lg:min-h-[20px]">
                        <img
                            src="/images/campaigns/version-two/hero-banner.svg"
                            class="max-h-[277px] lg:max-h-full mx-auto lg:min-w-[466px] mt-4 lg:absolute lg:top-1/2 lg:-left-[63%] lg:transform lg:-translate-y-1/2"
                            alt="Hero banner"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div ref="quotesContainer" class="mt-[30px] lg:mt-[50px] mx-auto w-[95%] sm:w-[70%] md:w-[50%] lg:w-[90%] min-w-[290px] max-w-[1170px]">
            <h3 class="text-[22px] lg:text-[32px] font-bold leading-tight text-[#2C2C4F]">
                1. {{ $t('campaign_version_two.How many solar panels do you need') }}
            </h3>

            <div class="relative h-[55px] mt-2">
                <div class="w-[39px] h-full hidden lg:flex items-center absolute top-0 -left-[29px]">
                    <img
                        src="/images/campaigns/version-two/solar-panels-bubble-icon.svg"
                        alt="Solar panels bubble icon"
                    />
                </div>

                <div class="bg-white w-full lg:w-[470px] h-full flex items-center shadow border rounded-lg border-[#D8DEE8]">
                    <button
                        @click="pvOffer.$data.pvSolarPanelsNumber = Math.max(pvOffer.$data.pvSolarPanelsNumber - 1, 1)"
                        class="w-[30px] h-[30px] mx-5 border border-[#D8DEE8] rounded-full text-[18px] text-[#2C2C4F] leading-relaxed">
                        -
                    </button>

                    <div class="flex flex-1 h-full">
                        <div class="grid grid-cols-3 lg:grid-cols-2 w-full h-full items-center gap-2">
                            <input
                                v-model="pvOffer.$data.pvSolarPanelsNumber"
                                class="h-full text-right text-[26px] lg:text-[30px] font-bold text-[#2C2C4F]"
                                type="number"
                                readonly
                            />

                            <span class="text-[#2C2C4F] text-[14px] col-span-2 lg:col-span-1">
                                {{ $t('campaign_version_two.Solar panels') }}
                            </span>
                        </div>
                    </div>

                    <button
                        @click="pvOffer.$data.pvSolarPanelsNumber++"
                        class="w-[30px] h-[30px] mx-5 border border-[#D8DEE8] rounded-full text-[18px] text-[#2C2C4F] leading-relaxed">
                        +
                    </button>
                </div>
            </div>

            <p
                class="leading-relaxed text-[14px] lg:text-[16px] mt-5 text-[#2C2C4F]"
                v-html="$t('campaign_version_two.consumption_result', {
                    kwp: installationSize,
                    from: parseInt(installationSize * 0.9 * 1000),
                    to: parseInt(installationSize * 1.1 * 1000)
                })"
            />


            <h3 class="text-[22px] lg:text-[32px] mt-8 font-bold leading-tight text-[#2C2C4F]">
                2. {{ $t('campaign_version_two.Choose your panel below') }}
            </h3>

            <p
                class="leading-relaxed text-[14px] lg:text-[16px] mt-3 text-[#2C2C4F]"
                v-html="$t('campaign_version_two.choose_your_panel_subline')"
            />

            <!-- HORIZONTAL SCROLL SECTION -->
            <HorizontalScrollableContainer class="mt-6">
                <quote-v2
                    v-for="(solarPanel, index) in solarPanels"
                    :key="`solar-panel-quote-${index}`"
                    :quote-key="index"
                    :product-data="solarPanel"
                    :is-selected="pvOffer.$data.solarPanelSelected === solarPanel"
                    @select-product="pvOffer.changeSolarPanel(solarPanel)"
                />
            </HorizontalScrollableContainer>

            <h3 class="text-[22px] lg:text-[32px] mt-8 font-bold leading-tight text-[#2C2C4F]">
                3. {{ $t('campaign_version_two.Choose your inverter') }}
            </h3>

            <p
                class="leading-relaxed text-[14px] lg:text-[16px] mt-3 text-[#2C2C4F]"
                v-html="$t('campaign_version_two.choose_your_inverter_subline')"
            />

            <div
                class="mt-6 py-2.5 px-4 max-w-[470px] flex justify-between items-center rounded-md"
                :class="hybridInverter ? 'bg-[#E2F6EC]' : 'bg-[#A0BBBB] bg-opacity-10'">
                <div>
                    <span
                        class="text-[14px]"
                        :class="hybridInverter ? 'text-[#1EB26D]' : 'text-[#12122F]'">
                        {{ $t('campaign_version_two.My inverter needs to be hybrid') }}
                    </span>
                </div>

                <label class="flex items-center cursor-pointer">
                    <div class="relative">
                        <input
                            v-model="hybridInverter"
                            type="checkbox"
                            class="sr-only"
                        />

                        <div
                            class="block w-[40px] h-[24px] rounded-full"
                            :class="{'bg-[#1EB26D]': hybridInverter, 'bg-[#C1CDDF]': !hybridInverter}"
                        />

                        <div
                            class="absolute left-[3px] top-[3px] bg-white w-[18px] h-[18px] rounded-full transition"
                            :class="{'transform translate-x-[90%]': hybridInverter}"
                        />

                    </div>
                </label>
            </div>

            <HorizontalScrollableContainer class="mt-6" :key="filteredInverters().length">
                <quote-v2
                    v-for="(inverter, index) in filteredInverters()"
                    :key="`inverter-quote-${index}`"
                    :quote-key="index"
                    :product-data="inverter"
                    :is-selected="pvOffer.$data.inverterSelected === inverter"
                    @select-product="onChangeInverter(inverter)"
                />
            </HorizontalScrollableContainer>

            <div
                v-if="pvOffer.$data.inverterSelected"
                class="mt-6 py-2.5 px-4 max-w-[470px] flex items-center rounded-md select-none cursor-pointer"
                :class="{
                    'bg-gray-100': !hybridInverter && !!pvOffer.$data.inverterSelected?.is_hybrid === false,
                    'bg-[#F4D5D4]': addBattery,
                    'bg-[#E0EEFA]': !addBattery && (hybridInverter || !!pvOffer.$data.inverterSelected?.is_hybrid)
                }"
                @click="hybridInverter || !!pvOffer.$data.inverterSelected?.is_hybrid ? addBattery = !addBattery : null"
            >
                <div class="flex items-center">
                    <div
                        class="w-[20px] h-[20px] flex justify-center items-center rounded-full text-white font-semibold"
                        :class="{
                            'bg-gray-200': !hybridInverter && !!pvOffer.$data.inverterSelected?.is_hybrid === false,
                            'bg-[#EA4335]': addBattery,
                            'bg-[#399ADB]': !addBattery && (hybridInverter || !!pvOffer.$data.inverterSelected?.is_hybrid)
                        }"
                    >
                        <span
                            class="pt-0.5 pl-[1px] transition-all duration-200"
                            :class="addBattery ? 'rotate-45' : 'rotate-0'">
                            +
                        </span>
                    </div>

                    <span
                        class="text-[14px] ml-2.5"
                        :class="{
                            'text-gray-400': !hybridInverter && !!pvOffer.$data.inverterSelected?.is_hybrid === false,
                            'text-[#EA4335]': addBattery,
                            'text-[#108FE5]': !addBattery && (hybridInverter || !!pvOffer.$data.inverterSelected?.is_hybrid)
                        }"
                    >
                        {{ addBattery ? $t('campaign_version_two.Remove battery from installation') : $t('campaign_version_two.Add a battery to your installation') }}
                    </span>
                </div>
            </div>

            <HorizontalScrollableContainer
                v-if="addBattery && pvOffer.batteriesCompatible()?.length"
                class="mt-6">
                <quote-v2
                    v-for="(battery, index) in pvOffer.batteriesCompatible()"
                    :key="`battery-quote-${index}`"
                    :quote-key="index"
                    :product-data="battery"
                    :is-selected="pvOffer.$data.batterySelected === battery"
                    @select-product="onChangeBattery(battery)"
                />
            </HorizontalScrollableContainer>

            <!-- PRICE DETAILS -->
            <div ref="quoteSummary" class="mt-[80px] max-w-[870px] mx-auto bg-white rounded-lg">
                <div class="price-details-section flex justify-between items-center bg-white rounded-t-lg border-[1px] border-b-0 border-[#D8DEE8]">
                    <div class="flex flex-1 flex-col">
                        <p class="block lg:hidden px-4 pb-3 border-b-[1px] border-[#D8DEE8] text-[#2C2C4F] font-bold">
                            {{ $t('campaign_version_two.Detail of your quote') }}
                        </p>

                        <price-details-item-v2
                            v-if="pvOffer.$data.solarPanelSelected"
                            :title="$t('campaign_version_two.installation_size')"
                            :value="`${installationSize} kWp`"
                            class="text-[14px] lg:text-[16px]"
                        />

                        <price-details-item-v2
                            v-if="pvOffer.$data.solarPanelSelected"
                            :title="pvOffer.$data.solarPanelSelected.brand + ' ' + pvOffer.$data.solarPanelSelected.model"
                            :value="pvOffer.$data.pvSolarPanelsNumber"
                            class="text-[14px] lg:text-[16px]"
                        />

                        <price-details-item-v2
                            v-if="pvOffer.$data.inverterSelected"
                            :title="pvOffer.$data.inverterSelected.brand + ' ' + pvOffer.$data.inverterSelected.model"
                            :value="pvOffer.$data.inverterQuantity"
                            class="bg-[#F7F9FC] lg:bg-transparent text-[14px] lg:text-[16px]"
                        />

                        <price-details-item-v2
                            v-if="pvOffer.$data.batterySelected"
                            :title="pvOffer.$data.batterySelected.brand + ' ' + pvOffer.$data.batterySelected.model"
                            value="1"
                            class="bg-[#F7F9FC] lg:bg-transparent text-[14px] lg:text-[16px]"
                        />

                        <price-details-item-v2
                            :title="$t('campaign_version_two.mounting_material_long')"
                            :value="$t('campaign_version_two.Included')"
                            class="price-details-item-show-desktop text-[16px]"
                        />

                        <price-details-item-v2
                            :title="$t('campaign_version_two.mounting_material_short')"
                            :value="$t('campaign_version_two.Incl')"
                            class="price-details-item-show-mobile text-[14px]"
                        />

                        <price-details-item-v2
                            :title="$t('campaign_version_two.working_hours_transport')"
                            :value="$t('campaign_version_two.Included')"
                            class="text-[16px] price-details-item-show-desktop"
                        />

                        <price-details-item-v2
                            :title="$t('campaign_version_two.working_hours_transport')"
                            :value="$t('campaign_version_two.Incl')"
                            class="bg-[#F7F9FC] price-details-item-show-mobile text-[14px]"
                        />

                        <price-details-item-v2
                            :title="$t('campaign_version_two.inspection_long')"
                            :value="$t('campaign_version_two.Included')"
                            class="price-details-item-show-desktop"
                        />

                        <price-details-item-v2
                            :title="$t('campaign_version_two.inspection_short')"
                            :value="$t('campaign_version_two.Incl')"
                            class="price-details-item-show-mobile text-[14px]"
                        />

                        <div class="w-full h-[1px] bg-[#D8DEE8] my-5" />

                        <price-details-item-v2
                            :title="$t('campaign_version_two.Subtotal')"
                            :value="$numberHelper.formatMoneyObject(pvOffer.totalPriceNet(), true)"
                            class="text-[14px] lg:text-[16px]"
                        />

                        <price-details-item-v2
                            :title="$t('campaign_version_two.vat', { value: vatPercentage})"
                            :value="$numberHelper.formatMoneyObject(pvOffer.getVatAmount(), true)"
                            class="text-[14px] lg:text-[16px]"
                        />

                        <price-details-item-v2
                            :title="$t('campaign_version_two.Total')"
                            :value="$numberHelper.formatMoneyObject(pvOffer.totalPriceWithMarginWithVAT(), true)"
                            class="text-[14px] lg:text-[16px]"
                            font-emphasized
                        />
                    </div>

                    <div class="w-[230px] h-[330px] ml-[80px] hidden relative lg:flex justify-end items-end flex-shrink-0">
                        <img
                            :src="ensureImageSrcSlash(pvOffer.$data.solarPanelSelected.image_url)"
                            class="h-[95%] object-cover"
                        />

                        <img
                            :src="ensureImageSrcSlash(pvOffer.$data.inverterSelected.image_url)"
                            class="price-details-inverter-image object-contain"
                            alt="Inverter image"
                        />
                    </div>
                </div>

                <div class="price-details-section last flex flex-col rounded-b-lg border-[1px] border-[#A3CFEC] lg:border-[#1EB26D]">
                    <div class="inner flex flex-col lg:flex-row justify-between lg:items-center">
                        <div class="flex flex-1 flex-col">
                            <price-details-item-v2
                                :title="$t('campaign_version_two.goverment_premium')"
                                :value="$numberHelper.formatMoneyObject(-1 * pvOffer.getSubsidyAmount(), true)"
                                value-string-color="#00A842"
                                class="text-[15px] lg:text-[16px]"
                            />

                            <p class="block lg:hidden px-4 py-2 text-[12px] text-[#959BB5]">
                                {{ $t('campaign_version_two.price_details_subline') }}
                            </p>

                            <price-details-item-v2
                                :title="$t('campaign_version_two.Your own investment')"
                                :value="$numberHelper.formatMoneyObject(pvOffer.ownInvestmentTotal(), true)"
                                font-emphasized
                                class="mt-2 text-[16px] lg:text-[20px]"
                            />
                        </div>

                        <div class="send-my-quote-btn w-full mt-4 lg:mt-0 lg:w-[230px] lg:ml-[80px] lg:flex justify-end flex-shrink-0">
                            <button class="bg-[#1EB26D] text-white w-full lg:w-auto font-semibold rounded-full py-2 px-6 hover:bg-opacity-90">
                                {{ $t('campaign_version_two.Send my quote') }}
                            </button>
                        </div>
                    </div>

                    <p class="mt-4 pl-4 text-[#595E74] text-[12px] hidden lg:block">
                        * {{ $t('campaign_version_two.price_details_subline') }}
                    </p>
                </div>
            </div>

             <!-- REVIEWS CAROUSEL -->
            <div class="mt-[70px]">
                <ReviewsCarousel/>
            </div>

            <!-- FAQ -->
            <div class="mt-[70px]">
                <p class="faq-title text-[22px] sm:text-[28px] font-bold sm:leading-loose text-[#2C2C4F]">
                    {{ $t("campaign_version_two.Frequently Asked Questions") }}
                </p>

                <div class="mt-[38px] grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-y-6 lg:gap-x-12">
                    <FaqItem v-for="faq in faqs" :key="faq.title">
                        <template v-slot:title>
                            {{ faq.title }}
                        </template>

                        {{ faq.content }}
                    </FaqItem>
                </div>
            </div>

            <footer class="mt-[70px] flex flex-col lg:flex-row lg:items-center pt-4 pb-8">
                <div class="w-[100px] shrink-0 mt-6">
                    <img
                        src="/images/campaigns/logo.svg"
                        class="w-full"
                        alt="Nova logo"
                    />
                </div>

                <p
                    class="lg:ml-10 mt-6 text-[14px] lg:text-[16px] lg:leading-loose text-[#141430]"
                    v-html="$t('campaign_version_two.footer_text')"
                />
            </footer>
        </div>

        <button
            v-if="showPriceButton"
            ref="priceButton"
            @click="goToQuoteSummary"
            class="price-btn inline-flex flex-col items-center shadow-lg rounded-full bg-[#108FE5] text-white">
            <span class="text-[14px]">{{ $t('campaign_version_two.Your price') }}</span>

            <span class="font-bold text-[24px]">{{ buttonPriceData.value }} <span class="text-[16px]">{{ buttonPriceData.suffix }}</span></span>
        </button>
    </CampaignLayoutV2>
</template>

<script>
import CampaignLayoutV2 from "@/Layouts/CampaignV2";
import SliderCheckListItem from "@/Components/Campaigns/SliderCheckListItem";
import HorizontalScrollableContainer from "@/Components/Campaigns/HorizontalScrollableContainer";
import QuoteV2 from "@/Components/Campaigns/QuoteV2";
import PriceDetailsItemV2 from "@/Components/Campaigns/PriceDetailsItemV2";
import ReviewsCarousel from "@/Components/Campaigns/ReviewsCarousel";
import FaqItem from "@/Components/Campaigns/FaqItem";

import PvOfferModel from "@/models/PvOfferModel";
import { reactive } from "vue";

export default {
    props: {
        errors: {
            type: Object,
            required: false,
        },
        auth: {
            type: Object,
            required: false,
        },
        toast: {
            type: Object,
            required: false,
        },
        locale: {
            type: String,
            required: true,
        },
        annualConsumption: {
            type: Number,
            required: false,
        },
        roofSheathings: {
            type: Array,
            required: true,
        },
        priceFactors: {
            type: Array,
            required: true,
        },
        minimumCompanyMargins: {
            type: Array,
            required: true,
        },
        baseMargins: {
            type: Array,
            required: true,
        },
        solarPanels: {
            type: Array,
            required: true,
        },
        inverters: {
            type: Array,
            required: true,
        },
        defaultSolarPanelId: {
            type: Number,
            required: true,
        },
        defaultInverterId: {
            type: Number,
            required: true,
        },
        batteries: {
            type: Array,
            required: true,
        },
        discount: {
            type: Number,
            required: false,
            default: () => 0,
        },
        userExtraPremium: {
            type: Number,
            required: false,
            default: () => 0,
        },
        discountExpirationDate: {
            type: String,
            required: true,
        },
    },
    components: {
        CampaignLayoutV2,
        SliderCheckListItem,
        HorizontalScrollableContainer,
        QuoteV2,
        PriceDetailsItemV2,
        ReviewsCarousel,
        FaqItem,
    },
    setup(props) {

        const offerBasis = reactive({
            roof_sheathing: null,
            lightning_protection: false,
            grounding: false,
            digging: false,
            digging_length: 0,
            lift: false,
            stuck_tiles: false,
            snow_guards: false,
            lightning_rod_removal: false,
            insurance: false,
            imageUpload: null,
            annualConsumption: props.annualConsumption,
            clientName: null,
            clientAddress: null,
            pdId: null,
            mpSubsidy: false,
            vat_level: (props.locale && props.locale === 'pl') ? 1.08 : 1.06,
        })

        let subsidy

        if (props.locale === 'nl') {
            subsidy = 'be_base'
        } else {
            subsidy = 'be_base'
        }

        const pvOffer = new PvOfferModel({
          defaultSolarPanelId: props.defaultSolarPanelId,
          defaultInverterId: props.defaultInverterId,
          roofSheathings: props.roofSheathings,
          priceFactors: props.priceFactors,
          userExtraPremium: props.userExtraPremium,
          offerBasis: offerBasis,
          samplePvOfferIndex: 1,
          allBatteries: props.batteries,
          allSolarPanels: props.solarPanels,
          allInverters: props.inverters,
          minimumCompanyMargins: props.minimumCompanyMargins,
          baseMargins: props.baseMargins,
          annualConsumption: props.annualConsumption,
          dynamicConsumptionMode: false,
          subsidy: subsidy,
          enablePowerOptimizers: false,
          powerOptimizersAuto: true,
          groundMounted: false,
          discount: props.discount,
          autoFillOptimizers: true
        });

        return {
            pvOffer,
            offerBasis
        };
    },
    data() {
        return {
            showPriceButton: true,
            heroChecklist: [
                this.$t('campaign_version_two.hero_check_list_item_1'),
                this.$t('campaign_version_two.hero_check_list_item_2'),
                this.$t('campaign_version_two.hero_check_list_item_3'),
            ],
            hybridInverter: false,
            addBattery: false,
            faqs: [
                {
                    title: this.$t('campaign_version_two.faq_title_item_1'),
                    content: this.$t('campaign_version_two.faq_content_item_1'),
                },
                {
                    title: this.$t('campaign_version_two.faq_title_item_2'),
                    content: this.$t('campaign_version_two.faq_content_item_2'),
                },
                {
                    title: this.$t('campaign_version_two.faq_title_item_3'),
                    content: this.$t('campaign_version_two.faq_content_item_3'),
                },
                {
                    title: this.$t('campaign_version_two.faq_title_item_4'),
                    content: this.$t('campaign_version_two.faq_content_item_4'),
                },
                {
                    title: this.$t('campaign_version_two.faq_title_item_5'),
                    content: this.$t('campaign_version_two.faq_content_item_5'),
                },
                {
                    title: this.$t('campaign_version_two.faq_title_item_6'),
                    content: this.$t('campaign_version_two.faq_content_item_6'),
                },
            ],
        };
    },
    created() {
        window.addEventListener('scroll', this.handleScroll);
    },
    destroyed() {
        window.removeEventListener('scroll', this.handleScroll);
    },
    watch: {
        hybridInverter(value) {
            if (!value) {
                const selectedInverter = this.inverters.find((inverter) => {
                    return inverter.id === this.defaultInverterId;
                })

                this.pvOffer.changeInverter(selectedInverter)

                this.pvOffer.$data.batterySelected = null

                this.addBattery = false
            } else {
                this.pvOffer.changeInverter(this.filteredInverters()[0])
            }
        },
        addBattery(value) {
            if (!value) {
                this.pvOffer.$data.batterySelected = null
            }
        },
    },
    computed: {
        installationSize() {
            return this.pvOffer.$data.pvSolarPanelsNumber * this.pvOffer.$data.solarPanelSelected.power_output / 1000
        },
        vatPercentage() {
            return parseInt((parseFloat(this.offerBasis.vat_level) - 1) * 100);
        },
        buttonPriceData() {
            return this.$numberHelper.formatMoneyObject(this.pvOffer.ownInvestmentTotal())
        },
    },
    methods: {
        handleScroll() {
            const viewportSection = this.$refs.quoteSummary;

            if (!viewportSection) {
                return;
            }

            const rect = viewportSection.getBoundingClientRect();

            const isInView = rect.top >= 0 && rect.top <= window.innerHeight ||
                rect.bottom >= 0 && rect.bottom <= window.innerHeight;

            this.showPriceButton = !isInView;
        },
        scrollToElement(element) {
            this.$nextTick(() => {
                this.$refs[element].scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            });
        },
        goToQuoteSummary() {
            this.scrollToElement('quoteSummary');
        },
        filteredInverters() {
            const inverters = this.pvOffer.suitableInverters().filter(inverter => (!this.hybridInverter || inverter.is_hybrid));

            return inverters.reduce((acc, item) => {
                if (item.series !== null && !acc.series.includes(item.series) && acc.kw <= this.installationSize) {
                    acc.series.push(item.series);
                    acc.data.push(item);
                } else if (item.series === null) {
                    acc.data.push(item);
                }

                return acc;
            }, { series: [], data: [] }).data
        },
        onChangeInverter(inverter) {
            if (!!inverter?.is_hybrid === false) {
                this.pvOffer.$data.batterySelected = null

                this.addBattery = false
            }

            this.pvOffer.changeInverter(inverter)
        },
        onChangeBattery(battery) {
            if (battery.id === this.pvOffer.$data.batterySelected?.id) {
                this.pvOffer.$data.batterySelected = null

                return
            }

            this.pvOffer.changeBattery(battery)
        },
        ensureImageSrcSlash(src) {
            return src[0] === "/" ? src : "/" + src;
        },
    },
};
</script>

<style scoped>
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.headline {
    margin: 10px 0px 20px 0px;
}

@media only screen and (min-width: 1024px) {
    .headline {
        margin: 20px 0px;
    }
}

.faq-title {
    text-align: left;
}

@media screen and (min-width: 1024px) {
    .faq-title {
        text-align: center;
    }
}

.price-details-section {
    padding: 20px 0px;
}

.price-details-section.last {
    background-color: rgba(16, 143, 229, 0.1);
}

.send-my-quote-btn {
    padding: 0px 16px;
}

.price-btn {
    min-width: 200px;
    position: fixed;
    bottom: 10px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 9999;
    padding: 5px 40px;
}

@media screen and (max-width: 410px) {
    .price-btn {
        padding: 5px 30px;
    }
}

.price-details-item-show-desktop {
    display: none !important;
}

.price-details-item-show-mobile {
    display: flex !important;
}

@media screen and (min-width: 1024px) {

    .price-details-item-show-desktop {
        display: flex !important;
    }

    .price-details-item-show-mobile {
        display: none !important;
    }
    .price-details-section {
        padding: 20px;
    }

    .price-details-section.last {
        background-color: rgba(30, 178, 109, 0.1);
    }

    .send-my-quote-btn {
        padding: 0px;
    }

}

.price-details-inverter-image {
    width: 120px;
    height: 120px;
    position: absolute;
    bottom: 0px;
    left: 0px;
}
</style>
